/**
 * Low-level HTTP error type used everywhere by lib/api/_client.ts.
 * Reads server's application/problem+json (RFC 7807) envelope when present.
 */
import { env } from "../env.js";

export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  errors?: Record<string, string[] | undefined>;
  [key: string]: unknown;
}

export class APIError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetail,
    public readonly url: string,
  ) {
    super(problem.title ?? `HTTP ${status}`);
    this.name = "APIError";
  }

  /** User-facing detail; safe to render in <ErrorState>. */
  get displayDetail(): string {
    return this.problem.detail ?? this.message;
  }

  /** Field validation errors keyed by path. */
  get fieldErrors(): Record<string, string[] | undefined> {
    return this.problem.errors ?? {};
  }
}

export const apiBaseUrl = env.VITE_API_URL;

/**
 * Derive a user-facing message from any thrown error, falling back to a
 * context-specific message. Use this instead of `error.problem?.detail ?? "..."`
 * so blank/missing backend details never render as an empty string.
 */
export function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof APIError) {
    const detail = error.problem.detail?.trim();
    if (detail) return detail;
    const title = error.problem.title?.trim();
    if (title && title !== `HTTP ${error.status}`) return title;
    return fallback;
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}
